import React, { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress, Snackbar, Alert } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { format } from "date-fns";
import { useLoaderData } from "react-router-dom";
import type { LoaderFunctionArgs } from "react-router-dom";

import { DynamicTable, TableColumn } from "../../components/common/table/Table";
import { PottingType } from "../../types/PottingTypes";
import { fetchLastPotting } from "../../services/PottingService";
import { PottingForm } from "../../components/common/form/PottingForm";

export async function loader(_: LoaderFunctionArgs) {
  const potts = await fetchLastPotting();
  return { potts };
}

export default function PottingPage() {
  const methods = useForm();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loaderData = useLoaderData() as { potts: PottingType[] };
  const initialPotts = useMemo(() => {
    try {
      return loaderData?.potts ?? [];
    } catch {
      return [];
    }
  }, [loaderData]);

  const [entries, setEntries] = useState<PottingType[]>(initialPotts);
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchLastPotting();
      setEntries(data);
    } catch (err: any) {
      setError(err.message);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialPotts.length === 0) {
      loadData();
    } else {
      setEntries(initialPotts);
    }
  }, [initialPotts]);

  const handleSuccess = () => {
    setShowSuccess(true);
    loadData();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setShowSnackbar(true);
  };

  const handleSnackbarClose = (
    _: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") return;
    setShowSnackbar(false);
  };

  return (
    <Box p={4}>
      <Box display="flex" flexDirection="row" gap={4} alignItems="flex-start">
        {/* Form Side */}
        <Box flex={1}>
          <FormProvider {...methods}>
            <PottingForm onSuccess={handleSuccess} onError={handleError} />
          </FormProvider>
        </Box>

        {/* Table Side */}
        <Box flex={3}>
          {loading ? (
            <CircularProgress />
          ) : (
            <DynamicTable columns={columns} data={entries} />
          )}
        </Box>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccess(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          Potting erfolgreich gespeichert!
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error || "Ein Fehler ist aufgetreten"}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const columns: TableColumn<PottingType>[] = [
  {
    key: "dateInProduction",
    label: "Produktionsdatum",
    render: (value) =>
      value ? format(new Date(value as string), "yyyy-MM-dd HH:mm:ss") : "-",
  },
  {
    key: "username",
    label: "Operator",
    render: (value) => value || "No username",
  },
  {
    key: "pottingBatch",
    label: "Charge-Nr Komponente",
    render: (value) => value || "-",
  },
  {
    key: "component",
    label: "A- oder B-Komponente",
    render: (value) => value || "-",
  },
  {
    key: "manufacturingDate",
    label: "Produktionsdatum Komponente",
    render: (value) =>
      value ? format(new Date(value as string), "yyyy-MM-dd") : "-",
  },
  {
    key: "isGoodEntrance",
    label: "Wareneingang?",
    render: (value) => (value ? "Ja" : "Nein"),
  },
];
